(() => {
  const instanceKey = '__pormidoSectionReveal';
  const main = document.getElementById('main-content');

  if (!main) return;

  // Guard against duplicate listeners if the asset is evaluated again in the theme editor.
  if (window[instanceKey] && typeof window[instanceKey].destroy === 'function') {
    window[instanceKey].destroy();
  }

  const pendingClass = 'section-reveal--pending';
  const visibleClass = 'section-reveal--visible';
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cleanupTasks = new Map();
  let observer = null;

  const isEligibleSection = (element) => (
    element instanceof HTMLElement
    && element.parentElement === main
    && element.classList.contains('shopify-section')
    && element.dataset.sectionReveal !== 'off'
  );

  const clearCleanupTask = (section) => {
    const task = cleanupTasks.get(section);
    if (!task) return;

    window.clearTimeout(task.timeoutId);
    section.removeEventListener('transitionend', task.onTransitionEnd);
    cleanupTasks.delete(section);
  };

  const finishReveal = (section) => {
    clearCleanupTask(section);
    section.classList.remove(pendingClass, visibleClass);
    section.dataset.sectionRevealState = 'revealed';
  };

  const reveal = (section, immediate = false) => {
    if (!isEligibleSection(section)) return;

    if (observer) observer.unobserve(section);
    clearCleanupTask(section);

    if (immediate || motionQuery.matches) {
      finishReveal(section);
      return;
    }

    section.dataset.sectionRevealState = 'revealing';
    section.classList.add(visibleClass);

    const onTransitionEnd = (event) => {
      if (event.target === section && (event.propertyName === 'opacity' || event.propertyName === 'transform')) {
        finishReveal(section);
      }
    };

    section.addEventListener('transitionend', onTransitionEnd);
    cleanupTasks.set(section, {
      onTransitionEnd,
      timeoutId: window.setTimeout(() => finishReveal(section), 780)
    });
  };

  const prepare = (section) => {
    if (!isEligibleSection(section) || section.dataset.sectionRevealState) return;

    if (motionQuery.matches || !observer) {
      finishReveal(section);
      return;
    }

    section.dataset.sectionRevealState = 'pending';
    section.classList.add(pendingClass);

    // Observe on the next frame so the initial translated state is painted before reveal.
    window.requestAnimationFrame(() => {
      if (section.isConnected && section.dataset.sectionRevealState === 'pending') {
        observer.observe(section);
      }
    });
  };

  const findSection = (event) => {
    if (isEligibleSection(event.target)) return event.target;
    const section = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
    return isEligibleSection(section) ? section : null;
  };

  const onSectionLoad = (event) => {
    const section = findSection(event);
    if (section) prepare(section);
  };

  const onSectionUnload = (event) => {
    const section = findSection(event);
    if (!section) return;
    if (observer) observer.unobserve(section);
    clearCleanupTask(section);
  };

  const onSectionSelect = (event) => {
    const section = findSection(event);
    if (section) reveal(section, true);
  };

  const onMotionPreferenceChange = (event) => {
    if (!event.matches) return;
    if (observer) observer.disconnect();
    main.querySelectorAll(`:scope > .shopify-section.${pendingClass}`).forEach((section) => reveal(section, true));
  };

  if ('IntersectionObserver' in window && !motionQuery.matches) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) reveal(entry.target);
      });
    }, {
      // Trigger as the section crosses 85% of the viewport (roughly 15% into view).
      rootMargin: '0px 0px -15% 0px',
      threshold: 0.01
    });
  }

  main.querySelectorAll(':scope > .shopify-section').forEach(prepare);

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:section:select', onSectionSelect);

  if (typeof motionQuery.addEventListener === 'function') {
    motionQuery.addEventListener('change', onMotionPreferenceChange);
  }

  window[instanceKey] = {
    destroy() {
      if (observer) observer.disconnect();
      cleanupTasks.forEach((task, section) => {
        window.clearTimeout(task.timeoutId);
        section.removeEventListener('transitionend', task.onTransitionEnd);
        finishReveal(section);
      });
      cleanupTasks.clear();
      document.removeEventListener('shopify:section:load', onSectionLoad);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      document.removeEventListener('shopify:section:select', onSectionSelect);
      if (typeof motionQuery.removeEventListener === 'function') {
        motionQuery.removeEventListener('change', onMotionPreferenceChange);
      }
    }
  };
})();
